<template>
  <div class="group-certificate-page">
    <!-- ===== Hero / Summary Header ===== -->
    <b-card
      no-body
      class="hero-card mb-1 overflow-hidden"
    >
      <div class="hero-inner">
        <div class="hero-text">
          <div class="hero-badge">
            <feather-icon
              icon="LayersIcon"
              size="18"
            />
            <span>Certificate Groups</span>
          </div>
          <h2 class="hero-title">
            Group Certificates
          </h2>
          <p class="hero-subtitle">
            Organize your certifications into logical groups. Each group bundles
            related certificates together with their issuing sources.
          </p>
        </div>

        <div class="hero-stats">
          <div class="stat-pill">
            <div class="stat-icon stat-icon--primary">
              <feather-icon
                icon="GridIcon"
                size="20"
              />
            </div>
            <div>
              <div class="stat-value">
                {{ totalRows }}
              </div>
              <div class="stat-label">
                Groups
              </div>
            </div>
          </div>

          <div class="stat-pill">
            <div class="stat-icon stat-icon--info">
              <feather-icon
                icon="AwardIcon"
                size="20"
              />
            </div>
            <div>
              <div class="stat-value">
                {{ totalCertificates }}
              </div>
              <div class="stat-label">
                Certificates
              </div>
            </div>
          </div>

          <div class="stat-pill">
            <div class="stat-icon stat-icon--success">
              <feather-icon
                icon="DatabaseIcon"
                size="20"
              />
            </div>
            <div>
              <div class="stat-value">
                {{ sourcesCount }}
              </div>
              <div class="stat-label">
                Sources
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <!-- ===== Toolbar : Search + Add ===== -->
    <b-card
      no-body
      class="toolbar-card mb-1"
    >
      <div class="toolbar-inner">
        <b-input-group class="search-group">
          <b-input-group-prepend is-text>
            <feather-icon icon="SearchIcon" />
          </b-input-group-prepend>
          <b-form-input
            v-model="search"
            placeholder="Search by group, certificate or source... (press Enter)"
            autocomplete="off"
            @keyup.enter="searchNow"
          />
          <b-input-group-append v-if="search">
            <b-button
              variant="outline-secondary"
              class="clear-search-btn"
              @click="clearSearch"
            >
              <feather-icon icon="XIcon" />
            </b-button>
          </b-input-group-append>
        </b-input-group>

        <b-button
          variant="primary"
          class="add-btn"
          @click="$router.push({ name: 'add-group-certificate' })"
        >
          <feather-icon
            icon="PlusIcon"
            size="16"
            class="mr-50"
          />
          Add Certificate Group
        </b-button>
      </div>
    </b-card>

    <!-- ===== Loading ===== -->
    <div
      v-if="dataLoad"
      class="text-center my-5 py-5"
    >
      <b-spinner
        type="grow"
        variant="primary"
        label="Loading..."
      />
      <p class="mt-1 text-muted">
        Loading certificate groups...
      </p>
    </div>

    <!-- ===== Empty state ===== -->
    <b-card
      v-else-if="!groups.length"
      class="text-center py-5"
    >
      <feather-icon
        icon="InboxIcon"
        size="48"
        class="text-muted mb-1"
      />
      <h4>No certificate groups found</h4>
      <p class="text-muted mb-1">
        {{ search ? 'Try adjusting your search.' : 'Start by creating your first certificate group.' }}
      </p>
      <b-button
        v-if="!search"
        variant="primary"
        @click="$router.push({ name: 'add-group-certificate' })"
      >
        <feather-icon
          icon="PlusIcon"
          size="16"
          class="mr-50"
        />
        Add Certificate Group
      </b-button>
    </b-card>

    <!-- ===== Content : group cards ===== -->
    <b-row
      v-else
      class="groups-grid"
    >
      <b-col
        v-for="group in groups"
        :key="group.id"
        cols="12"
        md="6"
        xl="4"
      >
        <div class="group-card">
          <div
            class="group-accent"
            :style="{ background: groupGradient(group) }"
          />

          <!-- Header -->
          <div class="group-card-head">
            <div
              class="group-avatar"
              :style="{ background: groupGradient(group) }"
            >
              <feather-icon
                icon="FolderIcon"
                size="22"
              />
            </div>
            <div class="group-head-text">
              <h4
                class="group-name"
                :title="groupName(group)"
              >
                {{ groupName(group) }}
              </h4>
              <span class="group-id">#{{ group.id }}</span>
            </div>
          </div>

          <div class="group-card-body">
            <!-- Sources -->
            <div class="group-section">
              <div class="section-label">
                <feather-icon
                  icon="DatabaseIcon"
                  size="14"
                  class="mr-25"
                />
                <span>Sources</span>
              </div>
              <div
                v-if="group.schema_owner && group.schema_owner.length"
                class="source-chips"
              >
                <span
                  v-for="(src, i) in group.schema_owner"
                  :key="i"
                  class="source-chip"
                  :style="{ background: sourceMeta(src).soft, color: sourceMeta(src).color }"
                >
                  <feather-icon
                    :icon="sourceMeta(src).icon"
                    size="12"
                    class="mr-25"
                  />
                  {{ src }}
                </span>
              </div>
              <span
                v-else
                class="empty-hint"
              >No source</span>
            </div>

            <!-- Certificates -->
            <div class="group-section">
              <div class="section-label">
                <feather-icon
                  icon="AwardIcon"
                  size="14"
                  class="mr-25"
                />
                <span>Certificates</span>
                <b-badge
                  pill
                  variant="light-primary"
                  class="ml-50"
                >
                  {{ (group.certificates || []).length }}
                </b-badge>
              </div>

              <div
                v-if="group.certificates && group.certificates.length"
                class="cert-chips"
              >
                <span
                  v-for="(cert, index) in group.certificates"
                  :key="cert.id"
                >
                  <div
                    v-b-popover.hover.top.html="getSpan(cert.name && cert.name.en, cert.name && cert.name.ar)"
                    class="cert-chip"
                  >
                    <span class="cert-chip-index">{{ index + 1 }}</span>
                    {{ getSmallName(cert.name && cert.name.en) }}
                  </div>
                </span>
              </div>
              <div
                v-else
                class="no-certs"
              >
                <feather-icon
                  icon="FileMinusIcon"
                  size="14"
                  class="mr-25"
                />
                No certificates in this group
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="group-card-footer">
            <button
              type="button"
              class="footer-btn footer-btn--edit"
              @click="goToEdit(group)"
            >
              <feather-icon
                icon="EditIcon"
                size="14"
                class="mr-25"
              />
              Edit
            </button>
            <button
              type="button"
              class="footer-btn footer-btn--delete"
              @click="deleteGroup(group.id)"
            >
              <feather-icon
                icon="Trash2Icon"
                size="14"
                class="mr-25"
              />
              Delete
            </button>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- ===== Pagination ===== -->
    <div
      v-if="!dataLoad && totalRows > perPage"
      class="pagination-wrapper"
    >
      <div class="pagination-wrapper__info">
        Showing
        <span class="font-weight-bold">{{ (page - 1) * perPage + 1 }}</span>
        -
        <span class="font-weight-bold">{{ Math.min(page * perPage, totalRows) }}</span>
        of
        <span class="font-weight-bold">{{ totalRows }}</span>
      </div>
      <b-pagination
        v-model="page"
        :total-rows="totalRows"
        :per-page="perPage"
        align="end"
        first-number
        last-number
        :total-visible="6"
        class="mb-0 custom-pagination"
        @input="getData"
      >
        <template #prev-text>
          <feather-icon
            icon="ChevronLeftIcon"
            size="16"
          />
        </template>
        <template #next-text>
          <feather-icon
            icon="ChevronRightIcon"
            size="16"
          />
        </template>
      </b-pagination>
    </div>
  </div>
</template>

<script>
/* eslint-disable camelcase */
import {
  BCard, BRow, BCol, BSpinner, BBadge, BButton, BPagination,
  BFormInput, BInputGroup, BInputGroupPrepend, BInputGroupAppend,
  VBPopover,
} from 'bootstrap-vue'
import axios from 'axios'
import { isString } from 'lodash'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BSpinner,
    BBadge,
    BButton,
    BPagination,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
  },
  directives: {
    'b-popover': VBPopover,
  },
  data() {
    return {
      dataLoad: false,
      search: '',
      groups: [],
      page: 1,
      perPage: 20,
      totalRows: 0,
    }
  },
  computed: {
    totalCertificates() {
      return this.groups.reduce(
        (acc, g) => acc + ((g.certificates && g.certificates.length) || 0),
        0,
      )
    },
    sourcesCount() {
      const set = new Set()
      this.groups.forEach(g => {
        (g.schema_owner || []).forEach(s => set.add(s))
      })
      return set.size
    },
  },
  mounted() {
    const q = this.$route.query
    if (q.search) this.search = q.search
    if (q.page) this.page = parseInt(q.page, 10) || 1
    this.getData(this.page)
  },
  methods: {
    groupName(group) {
      const { name } = group
      if (name && typeof name === 'object') return name.en || name.ar || '—'
      return name || '—'
    },
    sourceMeta(source) {
      const map = {
        SFDA: { icon: 'ShieldIcon', color: '#3b82f6', soft: 'rgba(59,130,246,0.12)' },
        'SFDA-EG': { icon: 'ShieldIcon', color: '#6366f1', soft: 'rgba(99,102,241,0.12)' },
        Jeem: { icon: 'AwardIcon', color: '#06b6d4', soft: 'rgba(6,182,212,0.12)' },
        Saber: { icon: 'PackageIcon', color: '#14b8a6', soft: 'rgba(20,184,166,0.12)' },
        Moiat: { icon: 'SettingsIcon', color: '#0ea5e9', soft: 'rgba(14,165,233,0.12)' },
        SLS: { icon: 'CheckCircleIcon', color: '#10b981', soft: 'rgba(16,185,129,0.12)' },
        GOEIC: { icon: 'GlobeIcon', color: '#0891b2', soft: 'rgba(8,145,178,0.12)' },
        MOPH: { icon: 'ActivityIcon', color: '#2563eb', soft: 'rgba(37,99,235,0.12)' },
        Halal: { icon: 'CheckSquareIcon', color: '#0d9488', soft: 'rgba(13,148,136,0.12)' },
      }
      return map[source] || {
        icon: 'TagIcon', color: '#5b8db8', soft: 'rgba(91,141,184,0.12)',
      }
    },
    groupGradient(group) {
      const gradients = [
        'linear-gradient(135deg, #60a5fa, #2563eb)',
        'linear-gradient(135deg, #22d3ee, #0891b2)',
        'linear-gradient(135deg, #34d399, #059669)',
        'linear-gradient(135deg, #818cf8, #4f46e5)',
        'linear-gradient(135deg, #2dd4bf, #0d9488)',
        'linear-gradient(135deg, #38bdf8, #0284c7)',
        'linear-gradient(135deg, #f093fb, #f5576c)',
        'linear-gradient(135deg, #f6d365, #fda085)',
      ]
      const key = `${group.id}`
      let hash = 0
      for (let i = 0; i < key.length; i += 1) {
        hash = (hash * 31 + key.charCodeAt(i)) % 1000000007
      }
      return gradients[Math.abs(hash) % gradients.length]
    },
    getSpan(nameEn, nameAr) {
      let html = ''
      if (nameEn) {
        html += `<div style="text-align: start; color: black"><strong>Name En</strong>: ${nameEn}</div>`
      }
      if (nameAr) {
        html += `<div style="text-align: start; color: black"><strong>Name Ar</strong>: ${nameAr}</div>`
      }
      return html || '<div style="text-align: start; color: gray">No name available</div>'
    },
    getSmallName(input) {
      if (!input) return '—'
      const words = input.trim().split(/\s+/)
      if (words.length <= 2) return input
      return `${words.slice(0, 2).join(' ')}...`
    },
    goToEdit(group) {
      this.$router.push({
        name: 'edit-group-certificate',
        params: { id: group.id, name: this.groupName(group) },
      })
    },
    deleteGroup(id) {
      this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
      }).then(result => {
        if (result.isConfirmed) {
          axios
            .delete(`certificate-categories/${id}`)
            .then(() => {
              this.$swal('Deleted!', 'Deleted Successfully.', 'success')
              this.getData(this.page)
            })
            .catch(error => {
              const payload = decryptData(error.response.data.payload).message
              let errorMessage = ''
              if (isString(payload)) {
                errorMessage = payload
              } else {
                const firstArrayKey = Object.keys(payload).find(key => Array.isArray(payload[key]))
                errorMessage = firstArrayKey ? payload[firstArrayKey][0] : 'An unknown error occurred'
              }
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: errorMessage,
                  icon: 'BellIcon',
                  variant: 'error',
                },
              })
            })
        }
      })
    },
    searchNow() {
      this.getData(1)
    },
    clearSearch() {
      this.search = ''
      this.getData(1)
    },
    syncFiltersToUrl() {
      const query = {}
      if (this.search) query.search = this.search
      if (this.page > 1) query.page = String(this.page)
      const isSame = JSON.stringify(this.$route.query) === JSON.stringify(query)
      if (!isSame) {
        this.$router.replace({ query }).catch(() => {})
      }
    },
    async getData(page = 1) {
      this.page = page
      this.syncFiltersToUrl()
      this.dataLoad = true
      try {
        const params = {
          skip: (page - 1) * this.perPage,
        }
        if (this.search) params.search = this.search

        const response = await axios.get('certificate-categories', { params })
        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          const data = responseData?.data || {}
          const listKey = Object.keys(data).find(key => Array.isArray(data[key]))
          this.groups = listKey ? data[listKey] : []
          this.totalRows = data.count || this.groups.length || 0
        }
      } catch (error) {
        if (error.response && error.response.status === 500) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Server Error',
              icon: 'BellIcon',
              variant: 'danger',
            },
          })
        }
      } finally {
        this.dataLoad = false
      }
    },
  },
}
</script>

<style lang="scss" scoped src="./GroupCertificate.scss"></style>
